// src/pages/Settings/AddBalance.jsx
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const AddBalance = () => {
  const { user, updateUser } = useAuth(); // Add updateUser from context
  const [amount, setAmount] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchCurrentBalance();
    fetchTransactionHistory();
  }, []);

  const fetchCurrentBalance = async () => {
    try {
      const response = await api.get('/admin/profile');
      const newBalance = response.data.admin.walletBalance || 0;
      setCurrentBalance(newBalance);
      
      // Update the auth context with the new balance
      if (user && updateUser) {
        updateUser({ walletBalance: newBalance });
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchTransactionHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get('/admin/transactions', {
        params: {
          type: 'CREDIT',
          limit: 10
        }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transaction history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!adminPassword) {
      toast.error('Please enter admin password');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/admin/add-balance', {
        amount: parseFloat(amount),
        adminPassword: adminPassword
      });

      if (response.data.success) {
        toast.success(`Successfully added ₹${amount} to your wallet`);
        
        // Get the new balance from response
        const newBalance = response.data.newBalance || response.data.admin?.walletBalance;
        
        if (newBalance !== undefined) {
          setCurrentBalance(newBalance);
          
          // Update the auth context with the new balance
          if (user && updateUser) {
            updateUser({ walletBalance: newBalance });
          }
        } else {
          // If new balance not in response, fetch it again
          await fetchCurrentBalance();
        }
        
        setAmount('');
        setAdminPassword('');
        await fetchTransactionHistory(); // Refresh transaction history
      } else {
        toast.error(response.data.error || 'Failed to add balance');
      }
    } catch (error) {
      console.error('Error adding balance:', error);
      toast.error(error.response?.data?.error || 'Failed to add balance');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="add-balance-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Add Balance</h1>
        </div>

        <div className="row">
          {/* Add Balance Form */}
          <div className="col-md-6 mb-4">
            <div className="admin-card-one">
              <div className="admin-card-header">
                <h2 className="admin-card-heading">Add Balance to Wallet</h2>
              </div>
              <div className="admin-card-body">
                <div className="current-balance mb-4 p-3 bg-light rounded">
                  <h5 className="mb-2">Current Balance</h5>
                  <h3 className="text-success mb-0">{formatCurrency(currentBalance)}</h3>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Points to Add (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter Points"
                      min="1"
                      step="0.01"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Admin Password</label>
                    <div className="password-input position-relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="form-control"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        className="position-absolute end-0 top-0 mt-2 me-2 border-0 bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ cursor: 'pointer', zIndex: 10 }}
                      >
                        <i className={`fas fa-eye${showPassword ? '-slash' : ''}`}></i>
                      </button>
                    </div>
                    <small className="text-muted">
                      Enter your admin password to confirm the transaction
                    </small>
                  </div>

                  <button
                    type="submit"
                    className="admin-submit-one w-100"
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Add Balance'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Transaction History */}
          <div className="col-md-6 mb-4">
            <div className="admin-card-one">
              <div className="admin-card-header">
                <h2 className="admin-card-heading">Recent Transactions</h2>
              </div>
              <div className="admin-card-body">
                {loadingHistory ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : transactions.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Points</th>
                          <th>Type</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((transaction) => (
                          <tr key={transaction._id}>
                            <td>{formatDate(transaction.createdAt)}</td>
                            <td className="text-success fw-bold">
                              +{formatCurrency(transaction.amount)}
                            </td>
                            <td>
                              <span className="badge bg-success">CREDIT</span>
                            </td>
                            <td>
                              <span className={`badge bg-${transaction.status === 'success' ? 'success' : 'danger'}`}>
                                {transaction.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    No transactions found
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBalance;